import {
  resolveTimeSeriesYAxisMode,
  type TimeSeriesYAxisMode,
} from "./y-axis-mode";

type VizRow = Record<string, unknown>;

export type TimeSeriesAxisState = {
  axisSeriesKeys: string[];
  hasPercentSeries: boolean;
  hasValueSeries: boolean;
  useDualAxis: boolean;
  useNormalizedAxis: boolean;
};

export type TimeSeriesStaticDotProps = {
  r: number;
  fill: string;
  stroke: string;
  strokeWidth: number;
};

/**
 * 可視化用の unknown 値を有限数値へ正規化する。
 *
 * @remarks
 * `1,234` のようなカンマ区切り文字列も許可し、数値解釈できない値は null を返す。
 */
export function toFiniteNumber(value: unknown): number | null {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null;
  }
  if (typeof value === "string") {
    const parsed = Number(value.split(",").join(""));
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

/**
 * 時系列チャートの軸モード判定を共通化する。
 *
 * @remarks
 * dual 軸は「percent 系と value 系が両方ある」場合だけ有効化し、
 * mode が `dual` でも混在しない場合は単一軸へフォールバックする。
 */
export function resolveTimeSeriesAxisState(args: {
  effectiveSeriesKeys: string[];
  fallbackSeriesKeys?: string[];
  percentKeySet: Set<string>;
  yAxisMode?: TimeSeriesYAxisMode | null;
}): TimeSeriesAxisState {
  const axisSeriesKeys =
    args.effectiveSeriesKeys.length > 0
      ? args.effectiveSeriesKeys
      : (args.fallbackSeriesKeys ?? []);
  const hasPercentSeries = axisSeriesKeys.some((key) =>
    args.percentKeySet.has(key),
  );
  const hasValueSeries = axisSeriesKeys.some(
    (key) => !args.percentKeySet.has(key),
  );
  const supportsDualAxis = hasPercentSeries && hasValueSeries;
  const resolvedYAxisMode = resolveTimeSeriesYAxisMode({
    mode: args.yAxisMode,
    metricGroupCount: axisSeriesKeys.length,
  });
  const useNormalizedAxis = resolvedYAxisMode === "normalized";

  return {
    axisSeriesKeys,
    hasPercentSeries,
    hasValueSeries,
    useDualAxis: supportsDualAxis && resolvedYAxisMode === "dual",
    useNormalizedAxis,
  };
}

/**
 * normalized 軸モード向けに系列値を 0-100 へ写像する。
 *
 * @remarks
 * 各系列は独立に min/max を計算して正規化する。
 * min=max の定数系列は 0 のみを 0、それ以外は 100 として扱う。
 * tooltip では元の実値も参照したいため、normalized 時だけ `<seriesKey>_original`
 * を同じ row に残して presenter 側が取り出せるようにする。
 */
export function normalizeRowsForAxis(args: {
  rows: VizRow[];
  axisSeriesKeys: string[];
  useNormalizedAxis: boolean;
  normalizationGroupByKey?: ReadonlyMap<string, string> | null;
}): VizRow[] {
  if (!args.useNormalizedAxis) return args.rows;

  const resolveNormalizationGroup = (seriesKey: string): string => {
    const groupKey = args.normalizationGroupByKey?.get(seriesKey);
    return groupKey ?? seriesKey;
  };

  const rangesByKey = new Map<
    string,
    { min: number; max: number; hasValue: boolean }
  >();
  for (const key of args.axisSeriesKeys) {
    const groupKey = resolveNormalizationGroup(key);
    if (rangesByKey.has(groupKey)) continue;
    rangesByKey.set(groupKey, {
      min: Number.POSITIVE_INFINITY,
      max: Number.NEGATIVE_INFINITY,
      hasValue: false,
    });
  }

  for (const row of args.rows) {
    for (const key of args.axisSeriesKeys) {
      const value = toFiniteNumber(row[key]);
      if (value === null) continue;
      const range = rangesByKey.get(resolveNormalizationGroup(key));
      if (!range) continue;
      range.hasValue = true;
      range.min = Math.min(range.min, value);
      range.max = Math.max(range.max, value);
    }
  }

  return args.rows.map((row) => {
    const normalizedRow: VizRow = { ...row };
    for (const key of args.axisSeriesKeys) {
      const value = toFiniteNumber(row[key]);
      if (value === null) continue;
      normalizedRow[`${key}_original`] = value;
      const range = rangesByKey.get(resolveNormalizationGroup(key));
      if (!range || !range.hasValue) continue;
      if (range.max === range.min) {
        normalizedRow[key] = value === 0 ? 0 : 100;
        continue;
      }
      normalizedRow[key] =
        ((value - range.min) / (range.max - range.min)) * 100;
    }
    return normalizedRow;
  });
}

/**
 * tooltip で percent 表記を適用する系列キー集合を返す。
 *
 * @remarks
 * normalized 軸でも tooltip 自体は実値を見せるため、percent 判定は元の field unit
 * だけを信頼する。
 */
export function resolveTooltipPercentFields(args: {
  axisSeriesKeys: string[];
  percentKeySet: Set<string>;
  useNormalizedAxis: boolean;
}): string[] {
  if (args.useNormalizedAxis) {
    return args.axisSeriesKeys.filter((key) => args.percentKeySet.has(key));
  }
  return [...args.percentKeySet];
}

/**
 * 各 series の有限値件数を key ごとに数える。
 *
 * @remarks
 * Recharts は `dot={false}` の series が有限値 1 点だけだと何も描かないため、
 * moving average のような疎な series を可視状態と整合させる判定材料を
 * renderer 間で共有する。
 */
export function buildTimeSeriesFinitePointCountMap(args: {
  rows: VizRow[];
  seriesKeys: readonly string[];
}): Map<string, number> {
  const counts = new Map<string, number>(
    args.seriesKeys.map((seriesKey) => [seriesKey, 0]),
  );

  for (const row of args.rows) {
    for (const seriesKey of args.seriesKeys) {
      if (toFiniteNumber(row[seriesKey]) === null) continue;
      counts.set(seriesKey, (counts.get(seriesKey) ?? 0) + 1);
    }
  }

  return counts;
}

/**
 * finite 値件数から、その series を plot/legend へ残すか判定する。
 *
 * @remarks
 * finite 値が 0 件の series は legend だけ出ても操作対象として意味が薄く、
 * 「選べるのに見えない」状態を作るため renderer 入口で除外する。
 */
export function hasRenderableTimeSeriesValue(
  count: number | undefined,
): boolean {
  return typeof count === "number" && count > 0;
}

/**
 * finite 値が 1 点しかない series 向けに静的 dot 設定を返す。
 *
 * @remarks
 * moving average の window が期間長に近いと、可視範囲内では 1 点だけしか
 * 算出されないことがある。`dot={false}` のままだと完全に見えなくなるため、
 * その場合だけ小さな dot を常時表示して plot/legend の不整合を防ぐ。
 */
export function resolveTimeSeriesStaticDotProps(args: {
  finitePointCount: number | undefined;
  color: string;
  radius: number;
}): false | TimeSeriesStaticDotProps {
  if (args.finitePointCount !== 1) return false;
  return {
    r: args.radius,
    fill: args.color,
    stroke: args.color,
    strokeWidth: 1.5,
  };
}

/**
 * 時系列チャートの数値 Y 軸幅を、実際の tick 文言に合わせて見積もる。
 *
 * @remarks
 * AI 可視化カードでは固定幅のままだと左側の余白が過剰になりやすいため、
 * 可視系列の値を formatter に通した最長ラベルから幅を概算して clamp する。
 * 値が存在しない場合でも 0 tick を基準に最低幅を確保する。
 */
export function resolveTimeSeriesNumericAxisWidth(args: {
  rows: VizRow[];
  seriesKeys: string[];
  tickFormatter: (value: unknown) => string;
  tickFontSize: number;
  isMobile: boolean;
}): number {
  const samples = new Set<number>([0]);
  for (const row of args.rows) {
    for (const key of args.seriesKeys) {
      const value = toFiniteNumber(row[key]);
      if (value === null) continue;
      samples.add(value);
    }
  }

  let maxLabelLength = 1;
  for (const value of samples) {
    const formatted = args.tickFormatter(value);
    maxLabelLength = Math.max(maxLabelLength, formatted.length);
  }

  const estimatedWidth = Math.ceil(maxLabelLength * args.tickFontSize * 0.64);
  const padding = args.isMobile ? 18 : 20;
  const minWidth = args.isMobile ? 36 : 40;
  const maxWidth = args.isMobile ? 52 : 56;

  return Math.max(minWidth, Math.min(maxWidth, estimatedWidth + padding));
}

/**
 * 軸ラベルの外側へ残す余白を、軸幅に応じて最小限へ丸める。
 *
 * @remarks
 * Recharts は Y 軸幅と chart margin を別々に消費するため、
 * dual 軸の右側に大きな固定 margin を置くと plot area が不必要に狭くなる。
 * 軸ラベルがカード端へ張り付きすぎない程度の breathing room だけ残し、
 * 余った幅はプロット領域へ返す。
 */
export function resolveTimeSeriesAxisOuterMargin(args: {
  axisWidth: number;
  isMobile: boolean;
}): number {
  const minMargin = args.isMobile ? 8 : 10;
  const maxMargin = args.isMobile ? 12 : 16;
  const scaledMargin = Math.round(args.axisWidth * 0.25);

  return Math.max(minMargin, Math.min(maxMargin, scaledMargin));
}
